import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  type StrictTranslation,
  type TranslationObject,
  TranslateLoader,
} from '@ngx-translate/core';
import { type Observable, catchError, forkJoin, map, of } from 'rxjs';
import { DEFAULT_LANG } from './language-storage.service';

type TranslationRecord = Record<string, TranslationObject | StrictTranslation | string>;

@Injectable()
export class FallbackTranslateLoader implements TranslateLoader {
  private readonly http: HttpClient = inject(HttpClient);
  public defaultLang: string = DEFAULT_LANG;

  getTranslation(lang: string): Observable<TranslationObject> {
    const fallbackTranslation$: Observable<TranslationObject> = this.loadTranslation(
      this.defaultLang,
    );

    if (lang === this.defaultLang) {
      return fallbackTranslation$;
    }

    return forkJoin({
      fallback: fallbackTranslation$,
      current: this.loadTranslation(lang).pipe(catchError(() => of({}))),
    }).pipe(
      map(({ fallback, current }: Record<'fallback' | 'current', TranslationRecord>) =>
        mergeFallbackTranslations(fallback, current),
      ),
    );
  }

  private loadTranslation(lang: string): Observable<TranslationObject> {
    return this.http.get<TranslationObject>(`/i18n/${lang}.json`);
  }
}

function mergeFallbackTranslations(
  fallback: TranslationRecord,
  current: TranslationRecord,
): TranslationObject {
  const merged: TranslationRecord = { ...fallback };

  for (const key of Object.keys(current)) {
    const currentValue: StrictTranslation = current[key];
    const fallbackValue: StrictTranslation = fallback[key];

    if (currentValue === '' && fallbackValue !== undefined) {
      merged[key] = fallbackValue;
      continue;
    }

    if (isRecord(currentValue) && isRecord(fallbackValue)) {
      merged[key] = mergeFallbackTranslations(fallbackValue, currentValue);
      continue;
    }

    merged[key] = currentValue;
  }

  return merged;
}

function isRecord(value: unknown): value is TranslationRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
